// TEBA Payment Receive: shared bits for the panel, the receipt dialog and the public receipt page.
import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { HPC_COLORS } from "../hpc/hpcTheme";
import { Field } from "./shared/PaymentShared";
import {
  escapeHtml,
  formatLabel,
  publicAssetUrl,
  receiptDocument,
  receiptRow,
  receiptSection,
  receiptTotalRow,
} from "./shared/paymentUtils";

export const TEBA_LOGO = "/assets/logo.jpg";

export const formatLkr = (amount) => {
  const n = Number(amount);
  if (amount === null || amount === undefined || Number.isNaN(n)) return "—";
  return `LKR ${n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-11-30" -> "30 Nov 2026" (no time zone shifts). */
export const formatDate = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

export const SUBSCRIPTION_TYPE_LABELS = { monthly: "Monthly", annual: "Annual", day: "Pay per day" };

// Status colours per theme mode: soft tinted pill, coloured text.
const STANDING_TONES = {
  paid: { light: "#2e7d32", dark: "#81c784" },
  expired: { light: "#c62828", dark: "#ef9a9a" },
  no_payment: { light: "#616161", dark: "#bdbdbd" },
  pay_per_day: { light: "#1565c0", dark: "#90caf9" },
  free_card: { light: "#00838f", dark: "#80deea" },
};

/** Whole months from the expiry month to this month (0 when it expired this month). */
const monthsSince = (expiryIso, today = new Date()) => {
  if (!expiryIso) return 0;
  const [year, month] = String(expiryIso).split("-").map(Number);
  return Math.max(0, (today.getFullYear() * 12 + today.getMonth() + 1) - (year * 12 + month));
};

/** "31 Oct" this year, "31 Oct 2027" otherwise. */
const shortDate = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number);
  const label = `${day} ${MONTHS[month - 1]}`;
  return year === new Date().getFullYear() ? label : `${label} ${year}`;
};

export const standingLabel = (standing, paidUntil, expiryDate) => {
  if (standing === "paid") return paidUntil ? `Paid until ${shortDate(paidUntil)}` : "Paid";
  if (standing === "expired") {
    const months = monthsSince(expiryDate);
    return months > 0 ? `Expired · ${months} mo` : "Expired";
  }
  if (standing === "no_payment") return "No payment";
  if (standing === "pay_per_day") return "Pay per day";
  if (standing === "free_card") return "Free card";
  return formatLabel(standing) || "—";
};

export const standingText = (standing, paidUntil) => {
  if (standing === "paid" && paidUntil) return `Paid until ${formatDate(paidUntil)}`;
  if (standing === "free_card") return "Free card – no payment due";
  return standingLabel(standing, paidUntil);
};

export const StandingChip = ({ standing, paidUntil, expiryDate, sx }) => {
  const theme = useTheme();
  const tone = (STANDING_TONES[standing] || STANDING_TONES.no_payment)[theme.palette.mode === "dark" ? "dark" : "light"];
  return (
    <Chip
      size="small"
      label={standingLabel(standing, paidUntil, expiryDate)}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: tone,
        backgroundColor: alpha(tone, theme.palette.mode === "dark" ? 0.16 : 0.1),
        border: `1px solid ${alpha(tone, 0.35)}`,
        "& .MuiChip-label": { px: 1 },
        ...sx,
      }}
    />
  );
};

// ---------------------------------------------------------------- receipt buttons (TEBA page)
const actionSx = { minWidth: 136, height: 32, fontWeight: 600, textTransform: "none" };
// Text and border follow the surrounding colour, so they read well on the success box in both modes.
const outlinedSx = {
  ...actionSx,
  color: "inherit",
  borderColor: "currentColor",
  "&:hover": { borderColor: "currentColor", backgroundColor: "action.hover" },
};
const primarySx = {
  ...actionSx,
  color: "#1a1200",
  backgroundColor: HPC_COLORS.hpuOrange,
  "&:hover": { backgroundColor: "#ffb84d" },
};

/** View / Print / Download PDF / Copy link after a payment is saved. */
export const TebaReceiptActions = ({ onView, onPrint, onPdf, onCopyLink, disabled }) => (
  <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={onView} disabled={disabled} sx={outlinedSx}>View receipt</Button>
    <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={onPrint} disabled={disabled} sx={outlinedSx}>Print receipt</Button>
    <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={onPdf} disabled={disabled} sx={primarySx}>Download PDF</Button>
    <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={onCopyLink} disabled={disabled} sx={outlinedSx}>Copy link</Button>
  </Box>
);

const rowSx = { minWidth: 64, height: 30, px: 1, fontWeight: 600, textTransform: "none" };

/** Small View / Print / PDF buttons for one recent payment. */
export const TebaRowReceiptButtons = ({ onView, onPrint, onPdf, disabled }) => {
  const stop = (handler) => (event) => { event.stopPropagation(); handler(); };
  const rowOutlined = {
    ...rowSx,
    color: "text.primary",
    borderColor: "divider",
    "&:hover": { borderColor: "text.secondary", backgroundColor: "action.hover" },
  };
  return (
    <Box display="flex" justifyContent="flex-end" gap={0.5} flexWrap="wrap">
      <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={stop(onView)} disabled={disabled} sx={rowOutlined}>View</Button>
      <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={stop(onPrint)} disabled={disabled} sx={rowOutlined}>Print</Button>
      <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={stop(onPdf)} disabled={disabled} sx={{ ...rowSx, ...primarySx, minWidth: 64, height: 30, px: 1 }}>PDF</Button>
    </Box>
  );
};

/** Amount breakdown: each month (arrears and current) with fee, late surcharge and total. */
export const BreakdownTable = ({ lines, total, totalLabel = "Total due" }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Period</TableCell>
          <TableCell align="right">Fee</TableCell>
          <TableCell align="right">Late surcharge</TableCell>
          <TableCell align="right">Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={`${line.kind}-${line.label}`}>
            <TableCell>
              {line.label}
              {line.kind === "arrears" && <Typography component="span" variant="caption" color="error.main"> · arrears</Typography>}
            </TableCell>
            <TableCell align="right">{formatLkr(line.fee)}</TableCell>
            <TableCell align="right">{line.surcharge ? formatLkr(line.surcharge) : "—"}</TableCell>
            <TableCell align="right">{formatLkr(line.total)}</TableCell>
          </TableRow>
        ))}
        {total !== undefined && (
          <TableRow>
            <TableCell colSpan={3} sx={{ fontWeight: 800 }}>{totalLabel}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 800 }}>{formatLkr(total)}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const familyMemberText = (receipt) => (receipt.family_members || [])
  .filter((m) => m.membership_id !== receipt.family_owner?.membership_id)
  .map((m) => `${m.full_name || "—"} (${m.membership_id || "—"})`)
  .join(", ");

/** Receipt body shown in the receipt dialog and on the public receipt page. */
export const TebaReceiptView = ({ receipt }) => (
  <Box sx={{ color: "text.primary" }}>
    <Box textAlign="center" mb={2}>
      <Box component="img" src={`${process.env.PUBLIC_URL}${TEBA_LOGO}`} alt="TEBA logo" sx={{ maxWidth: 150, maxHeight: 72, objectFit: "contain" }} />
      <Typography variant="h5" fontWeight={700}>TEBA</Typography>
      <Typography variant="body2" color="text.secondary">Payment Receipt</Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    <Grid container spacing={1.5}>
      <Field label="Receipt number" value={receipt.receipt_number} />
      <Field label="Payment date" value={formatDate(receipt.payment_date)} />
      <Field label="Branch" value={receipt.branch_name} />
      <Field label="Family owner" value={receipt.family_owner?.full_name} />
      <Field label="Member ID" value={receipt.family_owner?.membership_id} />
      {receipt.family_owner?.mobile_phone && <Field label="Mobile number" value={receipt.family_owner.mobile_phone} />}
      <Field label="Subscription" value={receipt.subscription?.name} />
      <Field label="Type" value={SUBSCRIPTION_TYPE_LABELS[receipt.subscription?.type] || receipt.subscription?.type} />
      <Field label="Payment for" value={receipt.payment_for} />
      <Field label="Amount paid" value={formatLkr(receipt.amount)} />
      <Field label="Payment method" value={formatLabel(receipt.payment_method)} />
      {receipt.payment_reference && <Field label="Payment reference" value={receipt.payment_reference} />}
      <Field label="Paid until" value={receipt.paid_until ? formatDate(receipt.paid_until) : "Pay per day (no expiry)"} />
      <Field label="Received by" value={receipt.received_by} />
      {receipt.remarks && <Field label="Remarks" value={receipt.remarks} />}
    </Grid>
    {familyMemberText(receipt) && (
      <Typography variant="body2" sx={{ mt: 2 }}><strong>Family members:</strong> {familyMemberText(receipt)}</Typography>
    )}
    {(receipt.period_lines || []).length > 0 && (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Period covered</Typography>
        <BreakdownTable lines={receipt.period_lines} />
      </Box>
    )}
    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
      Thank you for your payment. This is a computer generated receipt.
    </Typography>
  </Box>
);

/** Printable receipt (same page style as the HPU receipt, TEBA branding). */
export const tebaReceiptPrintHtml = (receipt) => {
  const row = receiptRow;
  const periods = (receipt.period_lines || []).map((line) => row(
    line.kind === "arrears" ? `${line.label} (arrears)` : line.label,
    line.surcharge
      ? `${formatLkr(line.fee)} + ${formatLkr(line.surcharge)} late surcharge = ${formatLkr(line.total)}`
      : formatLkr(line.total),
  )).join("");
  const others = familyMemberText(receipt);
  const body = [
    receiptSection("Receipt Information", `${row("Receipt Number", receipt.receipt_number)}${row("Payment Date", formatDate(receipt.payment_date))}${row("Branch", receipt.branch_name)}${row("Received By", receipt.received_by)}`),
    receiptSection("Member Information", `${row("Family Owner", receipt.family_owner?.full_name)}${row("Member ID", receipt.family_owner?.membership_id)}${receipt.family_owner?.mobile_phone ? row("Mobile Number", receipt.family_owner.mobile_phone) : ""}${others ? row("Family Members", others) : ""}`),
    receiptSection("Subscription", `${row("Subscription", receipt.subscription?.name)}${row("Type", SUBSCRIPTION_TYPE_LABELS[receipt.subscription?.type] || receipt.subscription?.type)}${row("Subscription Fee", formatLkr(receipt.subscription?.price))}`),
    periods ? receiptSection("Period Covered", periods) : "",
    receiptSection("Payment Information", `${row("Payment For", receipt.payment_for)}${row("Payment Method", formatLabel(receipt.payment_method))}${receipt.payment_reference ? row("Payment Reference", receipt.payment_reference) : ""}${receipt.remarks ? row("Remarks", receipt.remarks) : ""}${receiptTotalRow("Amount", formatLkr(receipt.amount))}${row("Paid Until", receipt.paid_until ? formatDate(receipt.paid_until) : "Pay per day (no expiry)")}`),
  ].filter(Boolean).join("\n");
  return receiptDocument({
    title: receipt.receipt_number,
    logoSrc: publicAssetUrl(TEBA_LOGO),
    logoAlt: "TEBA logo",
    brandHtml: "TEBA",
    subtitleHtml: escapeHtml("Payment Receipt"),
    body,
  });
};
